import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Truck, Image as ImageIcon, Star } from 'lucide-react';
import type { ShippingTypeToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useShippingTypesList, useShippingTypeMutations } from '~/hooks/use-shipping-types';
import { useCountriesList } from '~/hooks/use-countries';
import { useCountryRegionsList } from '~/hooks/use-country-regions';

const Bool = ({ on }: { on: boolean }) =>
	on ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>;

const num = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString('el-GR'));

/** Shipping types (Τρόποι Αποστολής) list — parity with the legacy admin grid; standalone editor on add/edit. */
export function ShippingTypesGridView() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { data, isLoading, isError, refetch } = useShippingTypesList();
	const { data: countries } = useCountriesList();
	const { data: regions } = useCountryRegionsList();
	const { remove } = useShippingTypeMutations();

	const [deleteRow, setDeleteRow] = useState<ShippingTypeToken | null>(null);

	const countryName = useMemo(() => {
		const map = new Map<number, string>();
		for (const c of countries ?? []) if (c.id != null) map.set(c.id, c.nm ?? '');
		return (id?: number | null) => (id != null ? (map.get(id) ?? String(id)) : '—');
	}, [countries]);

	// All available countries for a shipping type (BW-621); falls back to the legacy single cid.
	const countryNames = useMemo(() => {
		return (r: ShippingTypeToken) => {
			const ids = r.cids && r.cids.length > 0 ? r.cids : r.cid != null ? [r.cid] : [];
			return ids.length > 0 ? ids.map(id => countryName(id)).join(', ') : '—';
		};
	}, [countryName]);

	const regionName = useMemo(() => {
		const map = new Map<number, string>();
		for (const r of regions ?? []) if (r.id != null) map.set(r.id, r.nm ?? '');
		return (id?: number | null) => (id != null ? (map.get(id) ?? String(id)) : '—');
	}, [regions]);

	// All available regions for a shipping type (BW-622); falls back to the legacy single rid.
	const regionNames = useMemo(() => {
		return (r: ShippingTypeToken) => {
			const ids = r.rids && r.rids.length > 0 ? r.rids : r.rid != null ? [r.rid] : [];
			return ids.length > 0 ? ids.map(id => regionName(id)).join(', ') : '—';
		};
	}, [regionName]);

	const columns = useMemo<SystemGridColumn<ShippingTypeToken>[]>(
		() => [
			{
				key: 'image',
				header: t('orders.shippingTypes.columns.image'),
				width: '70px',
				toggleable: false,
				render: r =>
					r.img1 ? (
						<img src={r.img1} alt={r.nm ?? ''} className="h-9 w-9 rounded border border-border object-contain" />
					) : (
						<div className="flex h-9 w-9 items-center justify-center rounded border border-border bg-elevated">
							<ImageIcon size={14} className="text-foreground-disabled" />
						</div>
					)
			},
			{
				key: 'name',
				header: t('orders.shippingTypes.columns.name'),
				width: 'minmax(0,1.4fr)',
				sortValue: r => r.nm ?? '',
				exportValue: r => r.nm ?? '',
				render: r => (
					<span className="truncate text-sm font-medium text-foreground" title={r.nm ?? ''}>
						{r.nm || '—'}
					</span>
				)
			},
			{
				key: 'country',
				header: t('orders.shippingTypes.columns.countries'),
				width: '160px',
				sortValue: r => countryNames(r),
				exportValue: r => countryNames(r),
				render: r => (
					<span className="truncate text-sm text-foreground-muted" title={countryNames(r)}>
						{countryNames(r)}
					</span>
				)
			},
			{
				key: 'region',
				header: t('orders.shippingTypes.columns.regions'),
				width: '160px',
				sortValue: r => regionNames(r),
				exportValue: r => regionNames(r),
				render: r => (
					<span className="truncate text-sm text-foreground-muted" title={regionNames(r)}>
						{regionNames(r)}
					</span>
				)
			},
			{
				key: 'flatRate',
				header: t('orders.shippingTypes.columns.flatRate'),
				width: '120px',
				align: 'right',
				sortValue: r => r.frt ?? 0,
				exportValue: r => r.frt ?? '',
				render: r => <span className="text-sm tabular-nums text-foreground-muted">{num(r.frt)}</span>
			},
			{
				key: 'threshold',
				header: t('orders.shippingTypes.columns.threshold'),
				width: '130px',
				align: 'right',
				defaultVisible: false,
				sortValue: r => r.tprc ?? 0,
				exportValue: r => r.tprc ?? '',
				render: r => <span className="text-sm tabular-nums text-foreground-muted">{num(r.tprc)}</span>
			},
			{
				key: 'storeTakeAway',
				header: t('orders.shippingTypes.columns.storeTakeAway'),
				width: '110px',
				align: 'center',
				defaultVisible: false,
				sortValue: r => (r.stw ? 1 : 0),
				exportValue: r => (r.stw ? t('common.yes') : t('common.no')),
				render: r => <Bool on={Boolean(r.stw)} />
			},
			{
				key: 'default',
				header: t('orders.shippingTypes.columns.default'),
				width: '100px',
				align: 'center',
				sortValue: r => (r.isDefault ? 1 : 0),
				exportValue: r => (r.isDefault ? t('common.yes') : t('common.no')),
				render: r =>
					r.isDefault ? (
						<Star size={15} className="text-amber-500" fill="currentColor" />
					) : (
						<span className="text-foreground-disabled">—</span>
					)
			},
			{
				key: 'visible',
				header: t('orders.shippingTypes.columns.visible'),
				width: '90px',
				align: 'center',
				sortValue: r => (r.iv ? 1 : 0),
				exportValue: r => (r.iv ? t('common.yes') : t('common.no')),
				render: r => <Bool on={Boolean(r.iv)} />
			},
			{
				key: 'erpCode',
				header: t('orders.shippingTypes.columns.erpCode'),
				width: '120px',
				defaultVisible: false,
				sortValue: r => r.erpcd ?? '',
				exportValue: r => r.erpcd ?? '',
				render: r => <span className="truncate font-mono text-xs text-foreground-muted">{r.erpcd || '—'}</span>
			},
			{
				key: 'sort',
				header: t('orders.shippingTypes.columns.sort'),
				width: '80px',
				align: 'right',
				sortValue: r => r.si ?? 0,
				exportValue: r => r.si ?? 0,
				render: r => <span className="text-sm tabular-nums text-foreground-muted">{r.si ?? 0}</span>
			}
		],
		[t, countryNames, regionNames]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<ShippingTypeToken>
				gridId="shipping-types"
				title={t('orders.shippingTypes.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={r => r.id ?? r.nm}
				columns={columns}
				searchPredicate={(r, q) => `${r.nm ?? ''} ${r.cd ?? ''} ${r.erpcd ?? ''}`.toLowerCase().includes(q)}
				defaultSort={{ key: 'sort', dir: 'asc' }}
				emptyIcon={<Truck size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('orders.shippingTypes.add')}
				onAdd={() => navigate('/orders/shipping-methods/create')}
				onEdit={r => navigate(`/orders/shipping-methods/${r.id}`)}
				onDelete={setDeleteRow}
				exportFilename="shipping-types"
			/>

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('orders.shippingTypes.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('orders.shippingTypes.deleteBody', { name: deleteRow?.nm ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
